import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent, faListCheck, faBan } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classes from './receipt-actions-modal.module.scss';

export type ReceiptActionKey = 'globalDiscount' | 'itemDiscount' | 'oth' | 'cancel';

export type ReceiptActionsModalProps = {
    opened: boolean;
    onClose: () => void;
    onSelectAction: (key: ReceiptActionKey) => void;
    selectedQuantity?: number;
    maxQuantity?: number;
    onSelectedQuantityChange?: (quantity: number) => void;
    isTakeAway?: boolean;
    // Quick-charge bill: expose ONLY ביטול (cancel), hide every other action.
    isQuickCharge?: boolean;
    // Optional explicit card set + order (first = rightmost in RTL layout).
    allowedActions?: ReceiptActionKey[];
    isSingleItem?: boolean;
    singleItemName?: string;
};

const GiftIcon = () => (
    <svg width="34" height="34" viewBox="0 0 32 32" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13h22v4H5z" />
        <path d="M7 17v9h18v-9" />
        <path d="M16 13v13" />
        <path d="M16 13s-1.5-6-5-6-3 4 0 4 5 2 5 2zM16 13s1.5-6 5-6 3 4 0 4-5 2-5 2z" />
    </svg>
);

type CardDef = { key: ReceiptActionKey; title: string; toneClass: string; icon: IconProp | null; svgIcon?: React.ReactNode };

// RTL: first in array = rightmost on screen
const CARDS: CardDef[] = [
    { key: 'oth', title: 'על חשבון הבית', toneClass: classes.cardOth, icon: null, svgIcon: <GiftIcon /> },
    { key: 'globalDiscount', title: 'הנחה כללית', toneClass: classes.cardWhole, icon: faPercent as IconProp },
    { key: 'itemDiscount', title: 'הנחה לפריטים', toneClass: classes.cardItems, icon: faListCheck as IconProp },
    { key: 'cancel', title: 'ביטול פריטים', toneClass: classes.cardCancel, icon: faBan as IconProp },
];

const ReceiptActionsModalComponent: React.FC<ReceiptActionsModalProps> = ({
    opened,
    onClose,
    onSelectAction,
    selectedQuantity = 1,
    maxQuantity = 1,
    onSelectedQuantityChange,
    isTakeAway = false,
    isQuickCharge = false,
    allowedActions,
    isSingleItem = false,
    singleItemName = '',
}) => {
    if (!opened) return null;

    const cards = (() => {
        // allowedActions narrows the set; the QC/TA rules still apply on top of that subset.
        let base = CARDS;
        if (allowedActions && allowedActions.length > 0) {
            const cardByKey = new Map(CARDS.map((card) => [card.key, card] as const));
            base = allowedActions
                .map((key) => cardByKey.get(key) ?? null)
                .filter((card): card is CardDef => card !== null);
        }
        if (isQuickCharge) {
            return base.filter((card) => card.key === 'cancel');
        }
        if (isTakeAway) {
            return base.filter((card) => card.key !== 'cancel');
        }
        return base;
    })();
    const showSingleItemQuantity = isSingleItem && maxQuantity > 1;
    const canChangeQuantity = showSingleItemQuantity && onSelectedQuantityChange;
    const safeQuantity = Math.max(1, Math.min(selectedQuantity, maxQuantity));

    return (
        <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={isSingleItem ? 'פעולות פריט' : 'פעולות פריט'} dir="rtl">
            <div className={classes.modal}>
                <header className={classes.header}>
                    <span className={classes.title}>{isSingleItem ? 'פעולות פריט' : 'פעולות פריט'}</span>
                    <button
                        type="button"
                        className={classes.closeBtn}
                        onClick={onClose}
                        aria-label="סגור"
                        data-testid="receipt-actions-close"
                    >
                        ×
                    </button>
                </header>

                {showSingleItemQuantity ? (
                    <div className={classes.singleItemQuantity} aria-label="בחר כמות">
                        <div className={classes.singleItemSummary} data-testid="receipt-actions-single-item-summary">
                            <strong className={classes.singleItemName}>{singleItemName}</strong>
                            <span className={classes.singleItemTotal}>סה&quot;כ כמות בפריט: {maxQuantity}</span>
                        </div>
                        <h2 className={classes.quantityPrompt}>על כמה פריטים לבצע פעולה?</h2>
                        <div className={classes.quantityStepper}>
                            <button
                                type="button"
                                className={`${classes.quantityButton} ${classes.quantityDecrease}`}
                                aria-label="הפחת כמות"
                                data-testid="receipt-actions-single-item-quantity-dec"
                                disabled={!canChangeQuantity || safeQuantity <= 1}
                                onClick={() => onSelectedQuantityChange?.(safeQuantity - 1)}
                            >
                                −
                            </button>
                            <span className={classes.quantityValue} data-testid="receipt-actions-single-item-quantity">
                                {safeQuantity}
                            </span>
                            <button
                                type="button"
                                className={`${classes.quantityButton} ${classes.quantityIncrease}`}
                                aria-label="הגדל כמות"
                                data-testid="receipt-actions-single-item-quantity-inc"
                                disabled={!canChangeQuantity || safeQuantity >= maxQuantity}
                                onClick={() => onSelectedQuantityChange?.(safeQuantity + 1)}
                            >
                                +
                            </button>
                        </div>
                        <button
                            type="button"
                            className={classes.allButton}
                            data-testid="receipt-actions-single-item-quantity-all"
                            disabled={!canChangeQuantity || safeQuantity === maxQuantity}
                            onClick={() => onSelectedQuantityChange?.(maxQuantity)}
                        >
                            בחר הכל ({maxQuantity})
                        </button>
                    </div>
                ) : null}

                <div
                    className={classes.grid}
                    data-testid={isSingleItem ? 'receipt-actions-single-item' : 'receipt-actions-grid'}
                >
                    {cards.map((card) => (
                        <button
                            key={card.key}
                            type="button"
                            data-testid={`receipt-actions-card-${card.key}`}
                            className={`${classes.card} ${card.toneClass}`}
                            onClick={() => onSelectAction(card.key)}
                        >
                            <span className={classes.iconWrap}>
                                {card.svgIcon ?? <FontAwesomeIcon icon={card.icon!} />}
                            </span>
                            <span className={classes.cardTitle}>
                                {isSingleItem && card.key === 'cancel' ? 'ביטול פריט' : card.title}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ReceiptActionsModal = React.memo(ReceiptActionsModalComponent);
